import LeaderboardTable, {
  LeaderboardColumn,
} from '#/components/Leaderboard/shared/leaderboard-table/LeaderboardTable';
import { useLiquidityFillsState } from '@paradigm/features/src/leaderboard/contexts/liquidity-fills-list';
import { LiquidityFill } from '@paradigm/features/src/leaderboard/entities/liquidity';
import {
  HeaderCell,
  RankCell,
  FirmCell,
  PercentageCell,
  BigNumberCell,
  EmptyCell,
} from '#/components/Leaderboard/shared/leaderboard-table/cells';
import { bigNumberSortBy } from '#/components/Leaderboard/shared/leaderboard-table/sort';

const columns = getColumns();
export default function LiquidityFillsTable() {
  const { liquidityFills, status } = useLiquidityFillsState();

  return (
    <LeaderboardTable<LiquidityFill>
      data={liquidityFills}
      status={status}
      tableRowAlign="right"
      columns={columns}
      data-testid="leaderboard-liquidity-fills-table"
    />
  );
}

function getColumns(): LeaderboardColumn<LiquidityFill>[] {
  return [
    {
      id: 'rank',
      header: ({ column }) => <HeaderCell column={column} title="Rank" />,
      cell: ({ cell }) => (
        <RankCell
          data-testid={cell.column.id}
          isUserRow={cell.row.original.isFromUser}
        />
      ),
      size: 70,
      enableResizing: false,
      enableSorting: false,
      meta: { isMobileHeader: true, draggable: false },
    },
    {
      id: 'firm',
      accessorKey: 'firmName',
      header: ({ column }) => <HeaderCell column={column} title="Firm" />,
      size: undefined,
      cell: (props) => <FirmCell {...props} isLiquidityTab />,
      enableResizing: false,
      enableSorting: false,
      meta: { isMobileHeader: true, draggable: false },
    },
    {
      id: 'spacer',
      accessorKey: 'spacer',
      header: EmptyCell,
      cell: EmptyCell,
      enableResizing: false,
      enableSorting: false,
      meta: { isMobileHeader: true, draggable: false },
    },
    {
      id: 'total_received',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          tooltipContent="Number of RFQs your desk received."
          title="Total Received"
        />
      ),
      accessorKey: 'totalReceived',
      enableResizing: false,
      cell: BigNumberCell,
      size: 140,
      sortingFn: bigNumberSortBy('totalReceived'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
    {
      id: 'quote_rate',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          title="Quote Rate"
          tooltipContent="Number of RFQs your desk quoted / Number of RFQs your desk received."
        />
      ),
      accessorKey: 'quoteRate',
      enableResizing: false,
      size: 120,
      cell: PercentageCell,
      sortingFn: bigNumberSortBy('quoteRate'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
    {
      id: 'fill_rate',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          title="Fill Rate"
          tooltipContent={
            <>
              <div>
                Number of RFQs you traded / Total number of RFQs you received.
              </div>
              <span>
                Includes RFQs you did not quote and those that were cancelled.
              </span>
            </>
          }
        />
      ),
      accessorKey: 'fillRate',
      enableResizing: false,
      size: 110,
      cell: PercentageCell,
      sortingFn: bigNumberSortBy('fillRate'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
    {
      id: 'fill_to_quote_rate',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          title="Fill/Quote"
          tooltipContent={
            <>
              <div>
                Number of RFQs you traded / Total number of RFQs you quoted.
              </div>
              <span>This includes RFQs that were cancelled.</span>
            </>
          }
        />
      ),
      accessorKey: 'fillToQuoteRate',
      enableResizing: false,
      size: 120,
      cell: PercentageCell,
      sortingFn: bigNumberSortBy('fillToQuoteRate'),
      meta: { isMobileHeader: true, draggable: false },
      sortDescFirst: true,
      sortingDirection: 'desc',
    },
    {
      id: 'spread_rank',
      accessorKey: 'spreadRank',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          title="Spread Rank"
          tooltipContent={
            <>
              <span>Your average ranked bid/ask spread on quoted RFQs.</span>
              <ul css="padding-left: 20px;">
                <li>
                  For example, if you quote 2 RFQs and you were tightest spread
                  on one and second tightest spread on the other, your spread
                  score would show 1.5.
                </li>
                <li>
                  We then rank you again to get the final overall rank number
                  for average spread width.
                </li>
              </ul>
            </>
          }
        />
      ),
      sortingFn: bigNumberSortBy('spreadRank'),
      sortingDirection: 'asc',
      enableResizing: false,
      size: 130,
      cell: BigNumberCell,
      meta: { isMobileHeader: true, draggable: false },
    },
    {
      id: 'second_best_rate',
      accessorKey: 'secondBestRate',
      sortingFn: bigNumberSortBy('secondBestRate'),
      sortDescFirst: true,
      sortingDirection: 'desc',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          title="Second Best Rate"
          tooltipContent="Percentage of time you, as maker, are the 2nd best rate on Traded RFQs"
        />
      ),
      size: 160,
      cell: PercentageCell,
      enableResizing: false,
      meta: { isMobileHeader: true, draggable: false },
    },
    {
      id: 'total_volume',
      accessorKey: 'totalVolume',
      sortingFn: bigNumberSortBy('totalVolume'),
      sortDescFirst: true,
      sortingDirection: 'desc',
      header: ({ column }) => (
        <HeaderCell
          column={column}
          title="Total Volume"
          tooltipContent="Your total Maker + Taker volume on Paradigm"
        />
      ),
      size: 140,
      cell: BigNumberCell,
      enableResizing: false,
      isDefaultSorting: true,
      meta: { isMobileHeader: true, draggable: false },
    },
  ];
}
